import type { ExchangeMode, GameState } from '../types/GameState';

type TileExchangeState = Partial<Pick<GameState, 'exchangeMode' | 'swapsAllowed' | 'lastMove' | 'bag' | 'currentTurn'>>;
type LegacyTileExchangeRules = {
  tileExchangeAllowed?: boolean;
  allowTileExchange?: boolean;
  canExchangeTiles?: boolean;
  exchangeTilesAllowed?: boolean;
};
type TileExchangeRules = Partial<Pick<GameState, 'exchangeMode' | 'swapsAllowed'>> & LegacyTileExchangeRules;

export interface TileExchangeMessage {
  title: string;
  body: string;
  confirmLabel: string;
  cancellable: boolean;
  canExchange: boolean;
  exchangeEndsTurn: boolean;
}

export interface TileExchangeRuleSummary {
  mode: ExchangeMode;
  label: string;
  shortLabel: string;
  tone: 'positive' | 'warning';
}

const EXCHANGE_MODES: ExchangeMode[] = ['exchange_ends_turn', 'exchange_and_continue'];

export function getExchangeMode(gameOrRules: TileExchangeRules | null | undefined): ExchangeMode {
  if (gameOrRules?.exchangeMode && EXCHANGE_MODES.includes(gameOrRules.exchangeMode)) {
    return gameOrRules.exchangeMode;
  }

  const legacyAllowed =
    gameOrRules?.tileExchangeAllowed ??
    gameOrRules?.allowTileExchange ??
    gameOrRules?.canExchangeTiles ??
    gameOrRules?.exchangeTilesAllowed;

  if (legacyAllowed === true) return 'exchange_and_continue';
  if (legacyAllowed === false) return 'exchange_ends_turn';

  return gameOrRules?.swapsAllowed === 1 ? 'exchange_and_continue' : 'exchange_ends_turn';
}

export function getLegacySwapsAllowedForExchangeMode(mode: ExchangeMode) {
  return mode === 'exchange_and_continue' ? 1 : 0;
}

export function getTileExchangeEndsTurn(game: TileExchangeState | null | undefined, userId: string | null | undefined) {
  if (!game || !userId) return true;
  return getExchangeMode(game) === 'exchange_ends_turn';
}

export function isTileExchangeAllowed(game: TileExchangeRules | null | undefined) {
  const mode = getExchangeMode(game);
  return mode === 'exchange_ends_turn' || mode === 'exchange_and_continue';
}

export function hasUsedTileExchangeThisTurn(game: TileExchangeState | null | undefined, userId: string | null | undefined) {
  if (!game || !userId || getExchangeMode(game) !== 'exchange_and_continue') return false;
  return game.currentTurn === userId && game.lastMove?.type === 'swap' && game.lastMove.userId === userId;
}

export function getTileExchangeRuleSummary(gameOrRules: TileExchangeRules | null | undefined): TileExchangeRuleSummary {
  const mode = getExchangeMode(gameOrRules);

  if (mode === 'exchange_and_continue') {
    return {
      mode,
      label: 'Cambio y juego',
      shortLabel: 'Cambiar y jugar',
      tone: 'positive'
    };
  }

  return {
    mode,
    label: 'Cambio con pase',
    shortLabel: 'Cambiar = pasar',
    tone: 'warning'
  };
}

export function getTileExchangeMessage(
  game: TileExchangeState | null | undefined,
  userId: string | null | undefined,
  selectedTileCount = 0
): TileExchangeMessage {
  const exchangeMode = getExchangeMode(game);
  const bagTileCount = game?.bag?.length ?? 0;
  const hasEnoughTiles = bagTileCount > 0 && (selectedTileCount === 0 || bagTileCount >= selectedTileCount);
  const exchangeEndsTurn = exchangeMode === 'exchange_ends_turn';

  if (!hasEnoughTiles) {
    return {
      title: 'No quedan fichas suficientes',
      body: 'No quedan fichas suficientes en la bolsa para realizar este cambio.',
      confirmLabel: 'Entendido',
      cancellable: false,
      canExchange: false,
      exchangeEndsTurn
    };
  }

  if (hasUsedTileExchangeThisTurn(game, userId)) {
    return {
      title: 'Cambio ya utilizado',
      body: 'Ya has cambiado fichas en este turno. Ahora puedes jugar una palabra o pasar.',
      confirmLabel: 'Entendido',
      cancellable: false,
      canExchange: false,
      exchangeEndsTurn
    };
  }

  if (exchangeEndsTurn) {
    return {
      title: 'Cambiar fichas y pasar turno',
      body: 'Puedes cambiar las fichas seleccionadas por nuevas fichas de la bolsa. Según las normas de esta partida, al confirmar el cambio terminará tu turno y jugará tu oponente.',
      confirmLabel: 'Cambiar y pasar',
      cancellable: true,
      canExchange: true,
      exchangeEndsTurn
    };
  }

  return {
    title: 'Cambiar fichas',
    body: 'Puedes cambiar las fichas seleccionadas por nuevas fichas de la bolsa. Después del cambio podrás seguir jugando este turno.',
    confirmLabel: 'Cambiar fichas',
    cancellable: true,
    canExchange: true,
    exchangeEndsTurn
  };
}
